import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Hash, Building2, MessageSquare, Link2, Plus, Send, Users, Loader2 } from "lucide-react";

const TYPE_ICON = { channel: Hash, department: Building2, dm: MessageSquare, context: Link2 };

function initials(name) {
  return (name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function ConversationRow({ ch, active, onClick }) {
  const Icon = TYPE_ICON[ch.type] || Hash;
  const title = ch.display_name || ch.name;
  return (
    <button
      onClick={onClick}
      data-testid={`chat-conversation-${ch.id}`}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
        active ? "bg-primary text-primary-foreground" : "hover:bg-accent"
      }`}
    >
      {ch.type === "dm" ? (
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className={active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-primary/10 text-primary"}>
            {initials(title)}
          </AvatarFallback>
        </Avatar>
      ) : (
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${active ? "bg-primary-foreground/20" : "bg-muted"}`}>
          <Icon className="h-4 w-4" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{title}</p>
        <p className={`truncate text-xs ${active ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
          {ch.last_message || "No messages yet"}
        </p>
      </div>
    </button>
  );
}

function NewChannelDialog({ onCreate, pending }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" data-testid="new-channel-btn">
          <Plus className="h-3.5 w-3.5" /> Channel
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create channel</DialogTitle></DialogHeader>
        <div className="space-y-1.5">
          <Label>Channel name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. product-launch" data-testid="new-channel-name-input" />
        </div>
        <DialogFooter>
          <Button
            disabled={!name.trim() || pending}
            data-testid="new-channel-save-btn"
            onClick={() => onCreate(name.trim().toLowerCase().replace(/\s+/g, "-"), () => { setOpen(false); setName(""); })}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewDmDialog({ users, meId, onCreate, pending }) {
  const [open, setOpen] = useState(false);
  const [uid, setUid] = useState("");
  const others = users.filter((u) => u.id !== meId && u.role !== "customer");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" data-testid="new-dm-btn">
          <Users className="h-3.5 w-3.5" /> Direct
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Start a direct message</DialogTitle></DialogHeader>
        <div className="space-y-1.5">
          <Label>Teammate</Label>
          <Select value={uid} onValueChange={setUid}>
            <SelectTrigger data-testid="new-dm-user-select"><SelectValue placeholder="Select a teammate" /></SelectTrigger>
            <SelectContent>
              {others.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} · <span className="capitalize">{u.role?.replace(/_/g, " ")}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button disabled={!uid || pending} data-testid="new-dm-save-btn" onClick={() => onCreate(uid, () => { setOpen(false); setUid(""); })}>
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open chat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamChat() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);
  const appliedParam = useRef(false);

  const { data: channels = [] } = useQuery({
    queryKey: ["chat-channels"],
    queryFn: async () => (await api.get("/chat/channels")).data,
    refetchInterval: 15000,
  });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const active = useMemo(() => channels.find((c) => c.id === activeId), [channels, activeId]);

  useEffect(() => {
    const cParam = searchParams.get("c");
    if (cParam && !appliedParam.current) {
      appliedParam.current = true;
      setActiveId(cParam);
      return;
    }
    if (!activeId && channels.length) setActiveId(channels[0].id);
  }, [channels, activeId, searchParams]);

  const { data: messages = [], isLoading: msgLoading } = useQuery({
    queryKey: ["chat-messages", activeId],
    queryFn: async () => (await api.get(`/chat/channels/${activeId}/messages`)).data,
    enabled: !!activeId,
    refetchInterval: 8000,
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, activeId]);

  const sendMut = useMutation({
    mutationFn: (body) => api.post(`/chat/channels/${activeId}/messages`, { body }),
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["chat-messages", activeId] });
      qc.invalidateQueries({ queryKey: ["chat-channels"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not send message"),
  });

  const channelMut = useMutation({
    mutationFn: (name) => api.post("/chat/channels", { name, type: "channel" }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["chat-channels"] });
      setActiveId(res.data.id);
      toast.success("Channel created");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not create channel"),
  });

  const dmMut = useMutation({
    mutationFn: (uid) => api.post("/chat/dm", { user_id: uid }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["chat-channels"] });
      setActiveId(res.data.id);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not start chat"),
  });

  const send = () => { if (draft.trim() && !sendMut.isPending) sendMut.mutate(draft.trim()); };

  return (
    <div>
      <PageHeader title="Team Chat" subtitle="Channels, direct messages and deal-linked conversations" testid="team-chat-header" />
      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        {/* Conversation list */}
        <div className="flex flex-col rounded-xl border border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border p-3">
            <NewChannelDialog pending={channelMut.isPending} onCreate={(name, done) => channelMut.mutate(name, { onSuccess: done })} />
            <NewDmDialog users={users} meId={user?.id} pending={dmMut.isPending} onCreate={(uid, done) => dmMut.mutate(uid, { onSuccess: done })} />
          </div>
          <div className="max-h-[62vh] space-y-1 overflow-y-auto p-2" data-testid="chat-conversation-list">
            {channels.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">No conversations yet</p>
            ) : (
              channels.map((ch) => (
                <ConversationRow key={ch.id} ch={ch} active={ch.id === activeId} onClick={() => setActiveId(ch.id)} />
              ))
            )}
          </div>
        </div>

        {/* Message thread */}
        <div className="flex h-[70vh] flex-col rounded-xl border border-border bg-card">
          {!activeId ? (
            <EmptyState icon={MessageSquare} title="Select a conversation" description="Pick a channel or start a direct message to begin chatting." testid="chat-no-selection" />
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                {(() => { const Icon = TYPE_ICON[active?.type] || Hash; return (
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
                ); })()}
                <div className="min-w-0">
                  <p className="truncate font-heading text-sm font-semibold" data-testid="chat-active-title">{active?.display_name || active?.name || "Conversation"}</p>
                  <p className="text-xs capitalize text-muted-foreground">{active?.type || ""}{active?.department ? ` · ${active.department}` : ""}</p>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
                {msgLoading ? (
                  <p className="text-center text-sm text-muted-foreground">Loading messages…</p>
                ) : messages.length === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">No messages yet. Say hello 👋</p>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-message-${m.id}`}>
                        <Avatar className="h-7 w-7 shrink-0"><AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">{initials(m.sender_name)}</AvatarFallback></Avatar>
                        <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${mine ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-muted"}`}>
                          {!mine && <p className="mb-0.5 text-[11px] font-semibold opacity-70">{m.sender_name}</p>}
                          <p className="whitespace-pre-wrap break-words text-sm">{m.body}</p>
                          <p className={`mt-0.5 text-right text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{timeLabel(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="flex items-center gap-2 border-t border-border p-3">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={`Message ${active?.display_name || active?.name || ""}`}
                  data-testid="chat-composer-input"
                />
                <Button onClick={send} disabled={!draft.trim() || sendMut.isPending} className="rounded-full" data-testid="chat-send-btn">
                  {sendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
